import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePendingDeliveries } from "@/hooks/usePendingDeliveries";
import { DmBottomNav, DmHeader, UndoToast } from "@/components/delivery/DmChrome";
import { DmHome } from "@/components/delivery/DmHome";
import { DmQueue } from "@/components/delivery/DmQueue";
import { LoginsScreen } from "@/components/AgencyLogins";
import { DoneTab } from "@/components/DoneTab";
import { mask, shortId } from "@/components/PdPill";

export default function DeliveryApp() {
  const { user } = useAuth();
  if (!["delivery_man", "admin"].includes(user?.role)) return <Navigate to="/" replace />;
  return <DeliveryShell />;
}

function DeliveryShell() {
  const { user, logout } = useAuth();
  const [date, setDate] = useState(() => localStorage.getItem("delivery.date") || "today");
  const [hide, setHide] = useState(() => localStorage.getItem("delivery.hideLpgIds") === "1");
  const [last, setLast] = useState(null);
  const { data, load, mark, undo, busy } = usePendingDeliveries({ date });

  useEffect(() => localStorage.setItem("delivery.hideLpgIds", hide ? "1" : "0"), [hide]);
  useEffect(() => localStorage.setItem("delivery.date", date), [date]);
  const seenResolved = useRef(null);
  useEffect(() => {
    const list = data?.resolved_recent;
    if (!list) return;
    if (seenResolved.current) {
      list.filter((r) => !seenResolved.current.has(r.key)).forEach((r) =>
        toast.success(`✅ Complaint resolve hua · ${hide ? "••••" : shortId(r.lpg_id)} · ${r.resolution_note}`, { duration: 8000 }));
    }
    seenResolved.current = new Set(list.map((r) => r.key));
  }, [data, hide]);
  const clearLast = useCallback(() => setLast(null), []);
  const onMark = async (r, status) => {
    if (await mark(r, status, mask(r.lpg_id, hide))) setLast({ r, status, at: Date.now() });
  };
  const onUndo = async (r) => {
    if (await undo(r)) setLast(null);
  };
  const q = { data, date, setDate, hide, onMark, busy };

  return (
    <div className="min-h-screen bg-[#F4F3EC] pb-[calc(96px+env(safe-area-inset-bottom))]" data-testid="delivery-app">
      <DmHeader hide={hide} setHide={setHide} logout={logout} />
      <main className="max-w-xl mx-auto px-4 pt-5">
        <Routes>
          <Route index element={<DmHome data={data} user={user} date={date} />} />
          <Route path="pending" element={<DmQueue tab="pending" {...q} />} />
          <Route path="complaint" element={<DmQueue tab="complaint" {...q} />} />
          <Route path="done" element={<DoneTab compact hide={hide} isAdmin={false} company="all" onChanged={load} />} />
          <Route path="logins" element={<LoginsScreen logins={data?.logins} />} />
          <Route path="*" element={<Navigate to="/delivery" replace />} />
        </Routes>
      </main>
      <UndoToast last={last} hide={hide} onUndo={onUndo} onClose={clearLast} />
      <DmBottomNav t={data?.totals} />
    </div>
  );
}
